import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  LogOut,
  Users,
  Download as DownloadIcon,
  Search,
  Trash2,
  RefreshCcw,
  Mail,
  Sparkles,
  ArrowLeft,
  BarChart3,
  DollarSign,
  Loader2,
  Brain,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import BrandLogo from '../components/BrandLogo';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PWD_KEY = 'atw_admin_pwd';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function downloadCsv(leads) {
  const header = ['email', 'name', 'sources', 'download_count', 'created_at', 'last_seen'];
  const rows = leads.map((l) => [
    l.email || '',
    l.name || '',
    Array.isArray(l.sources) ? l.sources.join('|') : '',
    l.download_count ?? '',
    l.created_at || '',
    l.last_seen || '',
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `atw-leads-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function LoginCard({ onLogin, loading }) {
  const [pwd, setPwd] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!pwd.trim()) return;
    onLogin(pwd.trim());
  };
  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 relative overflow-hidden">
      {/* Ambient orbs */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
        style={{ background: 'radial-gradient(circle, #FF007F33 0%, transparent 70%)' }}
      />

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl"
      >
        <div className="flex items-center justify-center mb-6">
          <BrandLogo size={48} variant="stacked" showTagline={false} />
        </div>
        <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase tracking-[0.3em] text-white/50">
          <Lock className="w-3.5 h-3.5" /> Restricted area
        </div>
        <h1 className="text-3xl font-display font-black text-white mb-1">Admin Console</h1>
        <p className="text-sm text-white/60 mb-6">
          Enter the admin password to view captured leads and platform stats.
        </p>

        <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">
          Password
        </label>
        <Input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
          data-testid="admin-password-input"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 text-base"
          autoFocus
        />

        <Button
          type="submit"
          disabled={loading || !pwd.trim()}
          data-testid="admin-login-submit"
          className="mt-5 w-full h-12 text-base font-semibold text-black"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)',
          }}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" /> Unlock console
            </>
          )}
        </Button>

        <button
          type="button"
          onClick={() => (window.location.href = '/')}
          className="mt-5 mx-auto flex items-center gap-2 text-xs text-white/50 hover:text-white transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </button>
      </form>
    </div>
  );
}

function StatChip({ icon: Icon, label, value, accent = '#00F0FF' }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-40"
        style={{ background: accent }}
      />
      <div className="relative z-10 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
          <div className="text-3xl font-display font-black text-white">{value}</div>
        </div>
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState(() => localStorage.getItem(PWD_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState('');
  const [claudeTest, setClaudeTest] = useState({ loading: false, result: null, error: null });

  const authHeaders = useMemo(
    () => ({ 'X-Admin-Password': password, 'Content-Type': 'application/json' }),
    [password],
  );

  const handleLogin = async (pwd) => {
    setLoginLoading(true);
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) throw new Error('bad');
      localStorage.setItem(PWD_KEY, pwd);
      setPassword(pwd);
      setAuthed(true);
      toast({ title: 'Welcome back', description: 'Admin console unlocked.' });
    } catch {
      toast({
        title: 'Access denied',
        description: 'That password did not match. Try again.',
        variant: 'destructive',
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(PWD_KEY);
    setPassword('');
    setAuthed(false);
    setLeads([]);
    setStats(null);
  };

  const load = async (silent = false) => {
    if (!password) return;
    if (!silent) setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/admin/leads`, { headers: authHeaders }),
        fetch(`${API}/admin/stats`, { headers: authHeaders }),
      ]);
      if (r1.status === 401 || r2.status === 401) {
        logout();
        toast({
          title: 'Session expired',
          description: 'Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      const d1 = await r1.json();
      const d2 = await r2.json();
      setLeads(d1.leads || []);
      setStats(d2);
    } catch (e) {
      toast({
        title: 'Failed to load',
        description: String(e.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify stored password on mount
  useEffect(() => {
    (async () => {
      if (!password) return;
      try {
        const r = await fetch(`${API}/admin/leads`, {
          headers: { 'X-Admin-Password': password },
        });
        if (r.ok) {
          setAuthed(true);
        } else {
          localStorage.removeItem(PWD_KEY);
          setPassword('');
        }
      } catch {
        // Network error — allow retry via login screen
      }
    })();
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const testClaude = async () => {
    setClaudeTest({ loading: true, result: null, error: null });
    try {
      const r = await fetch(`${API}/test-claude`, {
        method: 'POST',
        headers: authHeaders,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Request failed');
      setClaudeTest({ loading: false, result: data, error: null });
      toast({ title: 'Claude is online', description: `Model ${data.model} · ${data.usage.output_tokens} tokens` });
    } catch (e) {
      const msg = String(e.message || e);
      setClaudeTest({ loading: false, result: null, error: msg });
      toast({ title: 'Claude test failed', description: msg, variant: 'destructive' });
    }
  };

  const removeLead = async (email) => {
    if (!window.confirm(`Delete lead ${email}?`)) return;
    try {
      const r = await fetch(`${API}/admin/leads/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error('failed');
      setLeads((prev) => prev.filter((l) => l.email !== email));
      toast({ title: 'Lead removed', description: email });
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: String(e.message || e),
        variant: 'destructive',
      });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        (l.email || '').toLowerCase().includes(q) ||
        (l.name || '').toLowerCase().includes(q) ||
        (Array.isArray(l.sources) ? l.sources.join(' ') : '').toLowerCase().includes(q),
    );
  }, [leads, query]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#030303] text-white font-sans">
        <Toaster />
        <LoginCard onLogin={handleLogin} loading={loginLoading} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      <Toaster />
      {/* Ambient background */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F33 0%, transparent 70%)' }}
      />

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/10 bg-black/30 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="admin-back-home"
              className="text-white/60 hover:text-white transition"
              title="Back to site"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <BrandLogo size={32} />
            <span className="hidden md:inline text-xs font-mono uppercase tracking-[0.35em] text-white/40 ml-2 border-l border-white/10 pl-4">
              Admin · Leads
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load()}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="text-white/70 hover:text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={testClaude}
              disabled={claudeTest.loading}
              data-testid="admin-test-claude-btn"
              className="text-white/70 hover:text-white"
            >
              {claudeTest.loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              Test Claude Connection
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadCsv(leads)}
              disabled={!leads.length}
              data-testid="admin-export-csv-btn"
              className="text-white/70 hover:text-white"
            >
              <DownloadIcon className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-white/70 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-10">
        {/* Claude test result panel */}
        {(claudeTest.result || claudeTest.error) && (
          <div
            data-testid="claude-test-result"
            className={`mb-6 rounded-2xl border p-5 backdrop-blur-xl ${
              claudeTest.error
                ? 'border-red-500/30 bg-red-500/5'
                : 'border-emerald-500/30 bg-emerald-500/5'
            }`}
          >
            <div className="flex items-start gap-3">
              {claudeTest.error ? (
                <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">
                    {claudeTest.error ? 'Claude connection failed' : 'Claude is online'}
                  </span>
                  {claudeTest.result && (
                    <>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
                        {claudeTest.result.model}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
                        in {claudeTest.result.usage.input_tokens} · out {claudeTest.result.usage.output_tokens} tok
                      </span>
                    </>
                  )}
                </div>
                {claudeTest.result?.response && (
                  <div className="mt-3 rounded-lg bg-black/40 border border-white/5 p-3 text-sm text-white/85 leading-relaxed whitespace-pre-wrap font-mono">
                    {claudeTest.result.response}
                  </div>
                )}
                {claudeTest.error && (
                  <div className="mt-2 text-sm text-red-300 font-mono">{claudeTest.error}</div>
                )}
              </div>
              <button
                onClick={() => setClaudeTest({ loading: false, result: null, error: null })}
                className="text-white/40 hover:text-white transition"
                aria-label="Dismiss"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1
            className="text-4xl md:text-5xl font-display font-black tracking-tight"
            style={{
              background: 'linear-gradient(90deg, #ffffff 0%, #a1a1aa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Lead intelligence
          </h1>
          <p className="text-white/60 mt-2 max-w-2xl">
            Every email captured via the free tools. Filter, export, or remove entries.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatChip
            icon={Mail}
            label="Total Leads"
            value={stats?.total_leads ?? '—'}
            accent="#00F0FF"
          />
          <StatChip
            icon={Users}
            label="Signed-in Users"
            value={stats?.total_users ?? '—'}
            accent="#FF007F"
          />
          <StatChip
            icon={BarChart3}
            label="AI Generations"
            value={stats?.total_generations ?? '—'}
            accent="#A855F7"
          />
          <StatChip
            icon={DollarSign}
            label="Paid Payments"
            value={stats?.total_paid_payments ?? '—'}
            accent="#22D3EE"
          />
        </div>

        {/* Sources */}
        {stats?.leads_by_source?.length ? (
          <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">
              Top sources
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.leads_by_source.map((s) => (
                <span
                  key={s.source}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/5"
                >
                  <span className="text-white">{s.source}</span>
                  <span className="ml-2 text-white/50">{s.count}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name, or source…"
              data-testid="admin-search-input"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
            />
          </div>
          <div className="text-xs text-white/50">
            {filtered.length} / {leads.length}
          </div>
        </div>

        {/* Table */}
        <div
          data-testid="admin-leads-table"
          className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left text-xs uppercase tracking-widest text-white/50">
                  <th className="px-5 py-3 font-normal">Email</th>
                  <th className="px-5 py-3 font-normal">Name</th>
                  <th className="px-5 py-3 font-normal">Sources</th>
                  <th className="px-5 py-3 font-normal text-right">Downloads</th>
                  <th className="px-5 py-3 font-normal">First seen</th>
                  <th className="px-5 py-3 font-normal">Last seen</th>
                  <th className="px-5 py-3 font-normal" />
                </tr>
              </thead>
              <tbody>
                {loading && !filtered.length && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-white/50">
                      <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
                      Loading leads…
                    </td>
                  </tr>
                )}
                {!loading && !filtered.length && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-white/50">
                      {leads.length === 0
                        ? 'No leads captured yet. Once visitors download from any free tool, they appear here.'
                        : 'No leads match your search.'}
                    </td>
                  </tr>
                )}
                {filtered.map((l) => (
                  <tr
                    key={l.email}
                    data-testid={`admin-lead-row-${l.email}`}
                    className="border-b border-white/5 hover:bg-white/[0.03] transition"
                  >
                    <td className="px-5 py-4 font-mono text-white">{l.email}</td>
                    <td className="px-5 py-4 text-white/80">{l.name || '—'}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(l.sources || []).map((s) => (
                          <span
                            key={s}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-white font-mono">
                      {l.download_count ?? 0}
                    </td>
                    <td className="px-5 py-4 text-white/60 whitespace-nowrap">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-5 py-4 text-white/60 whitespace-nowrap">
                      {formatDate(l.last_seen)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => removeLead(l.email)}
                        data-testid={`admin-delete-lead-${l.email}`}
                        className="text-white/40 hover:text-red-400 transition"
                        title="Delete lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-white/40">
          Around the World AI · Admin console · v1
        </div>
      </main>
    </div>
  );
}
